import { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ReactComponent as DownloadIcon } from 'assets/icons/download.svg';
import { ReactComponent as FilterIcon } from 'assets/icons/filter.svg';
import { ReactComponent as SearchIcon } from 'assets/icons/search.svg';
import Button from 'components/Button/Button';
import StatusDropButton from 'components/StatusDropButton/StatusDropButton';
import StatusDropButtonSelector from 'components/StatusDropButtonSelector/StatusDropButtonSelector';
import TextField from 'components/TextField/TextField';
import {
  RELATE_TO, RELATE_TO_MA, SEARCH, STATUS,
} from 'constants/formFields';
import { TASK_STATUS_OPTIONS } from 'constants/general';

import type ITaskFilters from '../interfaces/ITaskFilters';
import type IVCFilterOptions from '../interfaces/IVCFilterOptions';

import styles from '../sass/Filters.module.scss';

function TaskFilters({
  control, onSearchChange, valueChainList, onDownloadFile, materialityAssessmentList,
}: ITaskFilters) {
  const { t } = useTranslation();
  const [options, setOptions] = useState<IVCFilterOptions[]>([]);
  const [optionsMA, setOptionsMA] = useState<IVCFilterOptions[]>([]);
  useEffect(() => {
    setOptions(valueChainList.map((item) => ({
      id: item.id,
      name: item.name,
    })));
  }, [valueChainList]);

  useEffect(() => {
    if (materialityAssessmentList) {
      setOptionsMA(materialityAssessmentList?.map((item) => ({
        id: item.id,
        name: item.name,
      })));
    }
  }, [materialityAssessmentList]);

  return (
    <div className={styles.filters}>
      <div className={styles.search}>
        <Controller
          control={control}
          name={SEARCH}
          render={({ field: { name, value, onChange }, fieldState: { error } }) => (
            <TextField
              name={name}
              value={value}
              placeholder={t('common.searchByName')}
              error={error?.message}
              icon={<SearchIcon />}
              onChange={(event) => {
                onChange(event.target.value);
                onSearchChange({ search: event.target.value, onChange });
              }}
              required
            />
          )}
        />
      </div>
      <div className={styles.filtersWrapper}>
        <div className={styles.filterTitleIcon}>
          <FilterIcon />
          <span>{t('common.filterBy')}</span>
        </div>
        <div className={styles.selectors}>
          <Controller
            control={control}
            name={RELATE_TO_MA}
            render={({ field: { value, onChange } }) => (
              <StatusDropButtonSelector
                options={optionsMA}
                title={t('taskManagement.relatedToMA')}
                selectedOptions={value}
                onSelectionChange={onChange}
                clearAllSelected={() => onChange([])}
              />
            )}
          />
          <Controller
            control={control}
            name={RELATE_TO}
            render={({ field: { value, onChange } }) => (
              <StatusDropButtonSelector
                options={options}
                title={t('taskManagement.relatedTo')}
                selectedOptions={value}
                onSelectionChange={onChange}
                clearAllSelected={() => onChange([])}
              />
            )}
          />
          <Controller
            control={control}
            name={STATUS}
            render={({ field: { value, onChange } }) => (
              <StatusDropButton
                options={TASK_STATUS_OPTIONS}
                title={t('taskManagement.status')}
                selectedOptions={value?.filter((item): item is string => item !== undefined)}
                onSelectionChange={onChange}
                className={styles.buttonStatus}
                classNameList={styles.buttonStatusList}
                clearAllSelected={() => onChange([])}
                translate
                hideSearch
              />
            )}
          />
        </div>
        <Button
          title={t('dataSetsManagement.downloadButton')}
          startIcon={<DownloadIcon />}
          variant="ghost"
          onClick={onDownloadFile}
          className={styles.downloadButton}
        />
      </div>
    </div>
  );
}

export default TaskFilters;
